import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { TravelExplore } from '@mui/icons-material';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { ICourtAdminstrativeUnitAssignment } from '../../Interfaces/ICourtAdminstrativeUnitAssignment';
import {
  fetchBorisData,
  IBorisResult,
} from '../../hooks/reactQuerys/RealEstateQueries';

interface IProps {
  realEstate: ILegalTransactionRelationshipRealEstate;
  setRealEstate: (re: ILegalTransactionRelationshipRealEstate) => void;
  /** Zuordnung Gemarkung↔Amtsgericht (Rolle 3) zum Auflösen der Selects. */
  courtAdminAssignments: ICourtAdminstrativeUnitAssignment[];
}

const buildAddress = (re: ILegalTransactionRelationshipRealEstate): string => {
  const line1 = [re.Street, re.StreetNr].filter(Boolean).join(' ').trim();
  const line2 = [re.Postcode, re.City].filter(Boolean).join(' ').trim();
  return [line1, line2].filter(Boolean).join(', ').trim();
};

/** 5-stellige PLZ aus Postcode-Feld oder Adresstext ziehen. */
const extractPlz = (
  re: ILegalTransactionRelationshipRealEstate
): string | null => {
  const src = `${re.Postcode ?? ''} ${re.City ?? ''}`;
  const m = src.match(/\b(\d{5})\b/) || buildAddress(re).match(/\b(\d{5})\b/);
  return m ? m[1] : null;
};

/** Brandenburg-PLZ-Bereiche (BORIS ist nur für Brandenburg verfügbar). */
const isBrandenburgPlz = (plz: string): boolean => {
  const n = Number(plz);
  if (isNaN(n)) return false;
  const r = (lo: number, hi: number) => n >= lo && n <= hi;
  return (
    r(1941, 1998) || // Oberspreewald-Lausitz (BB-Teil)
    r(3000, 3253) || // Cottbus / Spree-Neiße
    r(4890, 4938) || // Elbe-Elster
    r(14400, 14999) || // Potsdam, Havelland, Teltow-Fläming
    r(15000, 16999) || // Oder-Spree, MOL, Barnim, Oberhavel, Uckermark, OPR
    r(17258, 17291) || // Uckermark (Grenzbereich)
    r(19300, 19357) // Prignitz
  );
};

const parseAreaToNumber = (raw?: string | null): number | null => {
  if (!raw) return null;
  const cleaned = String(raw)
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.]/g, '');
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
};

/**
 * BORIS/ALKIS-Abruf (nur Brandenburg): zieht zur Adresse Gemarkung/Flur/
 * Flurstück + Bodenrichtwert und befüllt damit Grundbuch-/Adressfelder. Reiner
 * Komfort — die Daten lassen sich danach manuell korrigieren.
 */
export const RealEstateBorisLookup: React.FC<IProps> = ({
  realEstate,
  setRealEstate,
  courtAdminAssignments,
}) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IBorisResult | null>(null);

  const address = buildAddress(realEstate);
  const plz = extractPlz(realEstate);
  // Issue 1: BORIS nur bei (erkennbarer) Brandenburg-Adresse anzeigen. Ist eine
  // PLZ erkennbar und NICHT Brandenburg → ausblenden.
  if (plz && !isBrandenburgPlz(plz)) return null;

  const canLookup = !!realEstate.Street && address.length >= 3;

  const onLookup = async () => {
    setLoading(true);
    setResult(null);
    try {
      setResult(await fetchBorisData(address));
    } finally {
      setLoading(false);
    }
  };

  const fs = result?.flurstueck;
  const brw = result?.bodenrichtwert;
  const koord = result?.koordinaten;
  const hasParcel =
    !!fs && !fs.fehler && (fs.gemarkung || fs.flur || fs.flurstuecksnummer);

  const onApply = () => {
    if (!fs) return;

    // Gemarkung-Name → idAdministrativeUnit + idCourt (Amtsgericht) über die
    // Zuordnungstabelle (Rolle 3 = Grundbuchamt). Bundesland leitet der Editor
    // aus dem Court ab.
    const gem = (fs.gemarkung ?? '').trim().toLowerCase();
    let idAdministrativeUnit: number | null = null;
    let idCourt: number | null = null;
    if (gem) {
      const match = courtAdminAssignments.find(
        a =>
          a.idCourtRole === 3 &&
          (a.AdministrativeUnit ?? '').trim().toLowerCase() === gem
      );
      if (match) {
        idAdministrativeUnit = match.idAdministrativeUnit;
        idCourt = match.idCourt;
      }
    }

    const prev = realEstate.LandParcelEntryArray?.[0];
    const entry = {
      // Neue id erzwingt Remount der Editor-Zeile → Selects ziehen die Werte.
      idRealEstateLandRegisterEntry: -Math.floor(Math.random() * 1e9) - 1,
      idCourt,
      idAdministrativeUnit,
      Court: null,
      LandRegister: fs.gemarkung ?? '',
      SheetLandRegister: prev?.SheetLandRegister ?? '',
      // Issue 3: Flur → Parcel, Flurstück → LandParcel (Editor-Konvention!)
      Parcel: fs.flur ?? '',
      LandParcel: fs.flurstuecksnummer ?? '',
    };
    const entries = [...(realEstate.LandParcelEntryArray ?? [])];
    if (entries.length > 0) entries[0] = entry;
    else entries.push(entry);

    const area = parseAreaToNumber(fs.flaeche_m2);
    setRealEstate({
      ...realEstate,
      LandParcelEntryArray: entries,
      ...(area != null ? { Area: area } : {}),
      // Koordinaten übernehmen → Karte ohne erneutes Geocoding + Breiten/Längengrad
      ...(koord?.lat != null ? { Latitude: koord.lat } : {}),
      ...(koord?.lon != null ? { Longitude: koord.lon } : {}),
    });
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
        <Button
          variant='outlined'
          size='small'
          startIcon={
            loading ? (
              <CircularProgress size={14} color='inherit' />
            ) : (
              <TravelExplore />
            )
          }
          disabled={!canLookup || loading}
          onClick={onLookup}
        >
          Katasterdaten abrufen (BORIS)
        </Button>
        <Typography variant='caption' color='text.secondary'>
          Brandenburg · zieht Gemarkung/Flur/Flurstück + Bodenrichtwert zur
          Adresse
        </Typography>
      </Stack>

      {!canLookup && (
        <Typography variant='caption' color='text.secondary'>
          Bitte zuerst Straße/Hausnummer (und idealerweise PLZ/Ort) erfassen.
        </Typography>
      )}

      {result?.fehler && (
        <Alert severity='warning' variant='outlined' sx={{ mt: 1 }}>
          {result.fehler}
        </Alert>
      )}

      {result && !result.fehler && (
        <Box
          sx={theme => ({
            mt: 1,
            p: 1.5,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          {fs?.fehler ? (
            <Alert severity='info' variant='outlined' sx={{ mb: 1 }}>
              Kein Flurstück gefunden: {fs.fehler}
            </Alert>
          ) : hasParcel ? (
            <Stack
              direction='row'
              spacing={1}
              flexWrap='wrap'
              useFlexGap
              sx={{ mb: 1 }}
            >
              {fs?.gemarkung && (
                <Chip size='small' label={`Gemarkung: ${fs.gemarkung}`} />
              )}
              {fs?.flur && <Chip size='small' label={`Flur: ${fs.flur}`} />}
              {fs?.flurstuecksnummer && (
                <Chip
                  size='small'
                  label={`Flurstück: ${fs.flurstuecksnummer}`}
                />
              )}
              {fs?.flaeche_m2 && (
                <Chip size='small' label={`Fläche: ${fs.flaeche_m2} m²`} />
              )}
            </Stack>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Kein Flurstück zur Adresse gefunden.
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          {brw?.bodenrichtwert_eur_m2 != null ? (
            <Typography variant='body2'>
              <strong>Bodenrichtwert:</strong> {brw.bodenrichtwert_eur_m2} €/m²
              {brw.stichtag ? ` (Stichtag ${brw.stichtag})` : ''}
              {brw.nutzungsart ? ` · ${brw.nutzungsart}` : ''}
            </Typography>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              {brw?.hinweis ?? brw?.fehler ?? 'Kein Bodenrichtwert gefunden.'}
            </Typography>
          )}

          {hasParcel && (
            <Button
              variant='contained'
              size='small'
              sx={{ mt: 1.5 }}
              onClick={onApply}
            >
              Kataster in Formular übernehmen
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};
